// SENDEKØ-STRIPA — «meldingene dine har ikke gått ut ennå».
//
// ── HVORFOR ─────────────────────────────────────────────────────────────────
// `data/sendeko.ts` retter en tapende sendevei: meldinger som ikke fikk plass på
// WebSocket-en forsvant stille. Men en kø som operatøren ikke SER er nesten like
// ille som ingen kø: hun tror meldingen er hos TOC-en, og TOC-en har den ikke.
//
// UX-gjennomgang 2026-08-18, funn **F7**: *«Brukeren vet ofte ikke om meldingen
// faktisk kom fram. I strid er usikkerhet om levering det motsatte av godfølelse.
// Konsekvens: folk dobbeltmelder på radio for sikkerhets skyld — da har systemet
// tapt.»* Stripa er svaret på den setningen: den sier hvor mange som venter, hvor
// lenge den eldste har ventet, og — det viktigste — **at ingenting er tapt**.
//
// ⚠ TO ULIKE ALVORSGRADER, OG DE MÅ IKKE SE LIKE UT.
//   • Ventende meldinger er NORMAL DRIFT på dårlig samband. Dempet.
//   • Overløp betyr TAPTE meldinger som må sendes på nytt. Rødt.
// Hadde de sett like ut, ville operatøren lest «12 venter» og «3 MISTET» som
// samme beskjed — og det er nøyaktig den likheten som lot samband-buggen stå i
// dagevis (bølge 210, «Levert» så ut som «Køet»).
import type { KoStatus } from '../data/sendeko.js';

export class KoStripe {
  readonly el: HTMLElement;

  constructor() {
    this.el = document.createElement('div');
    this.el.className = 'ko-stripe';
    this.el.hidden = true;
    this.el.setAttribute('role', 'status');
    // `polite`, ikke `assertive`: en kø er ikke en alarm. EMCON-stripa er
    // assertive fordi den styrer om noen får sende i det hele tatt; her er
    // beskjeden at systemet holder på noe, og den skal ikke avbryte opplesningen
    // av det operatøren faktisk jobber med.
    this.el.setAttribute('aria-live', 'polite');
  }

  /**
   * Oppdater stripa. `tekst` kommer fra `koTekst()` i `sendeko.ts`, så flata og
   * køen ikke kan si to ulike ting (§3).
   */
  oppdater(st: KoStatus, tekst: string): void {
    this.el.hidden = st.ventende === 0 && st.overlop === 0;
    this.el.textContent = tekst;
    this.el.classList.toggle('tap', st.overlop > 0);
    // Tittelen forklarer hva som skjer videre. Uten den er tallet en tilstand
    // uten handling: skal hun vente, eller skal hun ta radioen?
    this.el.title = st.overlop > 0
      ? 'Køen ble full og meldinger er MISTET. De kommer ikke ut av seg selv — send dem på nytt, om nødvendig på radio.'
      : 'Meldingene ligger lagret lokalt og sendes automatisk i det sambandet er tilbake. Du trenger ikke gjøre noe.';
  }
}
